import { AppointmentRepository } from '@/core/appointment/domain/AppointmentRepository.ts'
import {
  AppointmentModificationRequest,
  AppointmentOnlineCreationRequest,
  AppointmentsClient,
  CustomerRequest,
  ServiceRequest,
  VehicleRequest,
  WorkshopCampaignDto
} from 'online-customer-services-ts'
import {
  Appointment,
  AppointmentRequest,
  EditAppointmentRequest
} from '@/core/appointment/domain/Appointment.ts'
import { useTranslator } from 'sima-suite-translator-vue3'
import { createWorkshopDate } from '@/utils'

export class AppointmentRepositoryBackendClient implements AppointmentRepository {
  private readonly appointmentClient: AppointmentsClient

  constructor(url: string) {
    this.appointmentClient = new AppointmentsClient(url)
  }

  async createAppointment(request: AppointmentRequest): Promise<void> {
    const {
      adviser,
      customer,
      workshop,
      hostId,
      date,
      vehicle,
      services,
      campaigns,
      availability
    } = request
    const { language } = useTranslator()

    return await this.appointmentClient.createAppointment22(
      hostId,
      workshop.simaId.toString(),
      new AppointmentOnlineCreationRequest({
        adviserId: Number(adviser.id),
        cultureName: language.value,
        customer: new CustomerRequest({
          customerId: undefined,
          email: customer.email,
          mobile: customer.mobilePhone,
          name: customer.name,
          phone: customer.phone
        }),
        services: services.map(
          (service) =>
            new ServiceRequest({
              id: Number(service.id),
              remarks: service.remarks
            })
        ),
        workshopId: workshop.id,
        teamId: availability[date].teamId,
        vehicle: new VehicleRequest({
          brandCode: vehicle.brandCode,
          model: vehicle.model,
          vin: vehicle.vin ?? undefined,
          kms: vehicle.kilometers,
          plate: vehicle.plate
        }),
        when: createWorkshopDate(date, adviser.time),
        campaigns: campaigns.map(
          (campaign) =>
            new WorkshopCampaignDto({
              campaignCode: campaign.code,
              necessaryHours: campaign.hours,
              campaignName: campaign.name
            })
        )
      })
    )
  }

  async getAppointment(hostId: string, enterpriseId: string, id: string): Promise<Appointment> {
    const appointment = await this.appointmentClient.get(hostId, enterpriseId, id)

    const dateTime = appointment.appointmentDateTime?.split(' ') ?? []

    return {
      id: appointment.appointmentId ?? '',
      date: dateTime[0] ?? '',
      time: dateTime[1] ?? '',
      workshop: {
        id: appointment.workshop?.id ?? NaN,
        name: appointment.workshop?.name ?? '',
        addressPart1: appointment.workshop?.addressPart1 ?? '',
        addressPart2: appointment.workshop?.addressPart2 ?? '',
        startingWorkHour: appointment.workshop?.startingWorkHour ?? '',
        endingWorkHour: appointment.workshop?.endingWorkHour ?? '',
        schedulerTimeSlot: appointment.workshop?.schedulerTimeSlot ?? NaN,
        brands:
          appointment.workshop?.brands?.map((brand) => {
            return {
              code: brand.code ?? '',
              serviceId: brand.serviceId ?? NaN
            }
          }) ?? [],
        latitude: appointment.workshop?.latitude ?? NaN,
        longitude: appointment.workshop?.longitude ?? NaN,
        timeZoneId: appointment.workshop?.timeZoneId ?? '',
        noWorkingWeekDays: appointment.workshop?.noWorkingWeekDays ?? [],
        simaId: appointment.workshop?.simaId ?? NaN,
        idInEnterprise: appointment.workshop?.idInEnterprise ?? NaN,
        daysOfMarginToRequest: appointment.workshop?.daysOfMarginToRequest ?? NaN,
        phone: appointment.workshop?.phone ?? '',
        redirectUrl: appointment.workshop?.redirectUrl ?? ''
      },
      adviser: { name: appointment.adviserName ?? '' },
      vehicle: {
        brand: appointment.vehicle?.brand ?? '',
        brandCode: appointment.vehicle?.brandCode ?? '',
        model: appointment.vehicle?.model ?? '',
        vin: appointment.vehicle?.vin ?? '',
        kilometers: appointment.vehicle?.kms ?? NaN,
        plate: appointment.vehicle?.plate ?? ''
      },
      customer: {
        name: appointment.customer?.name ?? '',
        email: appointment.customer?.email ?? '',
        mobilePhone: appointment.customer?.mobile ?? '',
        phone: appointment.customer?.phone ?? ''
      },
      teamId: appointment.teamId ?? NaN,
      servicesTotalUdts: appointment.servicesTotalUdts ?? NaN
    }
  }

  async cancelAppointment(
    hostId: string,
    enterpriseId: string,
    appointmentId: string
  ): Promise<void> {
    return await this.appointmentClient.cancelAppointment(enterpriseId, appointmentId, hostId)
  }

  async editAppointment(request: EditAppointmentRequest): Promise<void> {
    const { adviser, appointmentId, hostId, teamId, date, workshop, enterpriseId } = request

    const { language } = useTranslator()

    const requestData = new AppointmentModificationRequest({
      workshopId: workshop.id,
      adviserId: Number(adviser.id),
      teamId,
      date: createWorkshopDate(date, adviser.time),
      cultureName: language.value
    })

    return await this.appointmentClient.modifyAppointment(
      enterpriseId,
      appointmentId,
      hostId,
      requestData
    )
  }
}
